// src/components/collections/CollectionAdjustment.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./collections.css";
import { useNavigate } from "react-router-dom";
import {
    fetchBillDateRanges,
    fetchSanklanByKnoRange,
    saveSanklan,
} from "../../services/sanklan";
import { useCustomerRepo, useRateRepo } from "../../repos/AppRepoProvider";

// ---------------- utils ----------------
function pad2(n) {
    const x = String(n);
    return x.length === 1 ? `0${x}` : x;
}

function todayIsoLocal() {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function isoToDmy(iso) {
    const s = String(iso || "").trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    return `${m[3]}/${m[2]}/${m[1]}`;
}

function fmt2(v) {
    const n = Number(v || 0);
    return n.toFixed(2);
}
function fmt1(v) {
    const n = Number(v || 0);
    return n.toFixed(1);
}

function shiftShort(time) {
    const t = String(time || "").toLowerCase();
    if (t === "morning") return "M";
    if (t === "evening") return "E";
    return "-";
}
function milkShort(type) {
    const t = String(type || "").toLowerCase();
    if (t === "cow") return "C";
    if (t === "buffalo") return "B";
    return "-";
}

function numStrOrZero(v) {
    const s = String(v ?? "").trim();
    if (!s) return "0";
    const n = Number(s);
    if (!Number.isFinite(n)) return "0";
    return String(n);
}

function mapTimeForServer(v) {
    const t = String(v || "").trim().toLowerCase();
    if (t === "morning" || t === "m") return "Morning";
    if (t === "evening" || t === "e") return "Evening";
    if (String(v) === "Morning" || String(v) === "Evening") return String(v);
    return "Morning";
}

function mapTypeForServer(v) {
    const t = String(v || "").trim().toLowerCase();
    if (t === "buffalo" || t === "b") return "Buffalo";
    if (t === "cow" || t === "c") return "Cow";
    if (String(v) === "Cow" || String(v) === "Buffalo") return String(v);
    return "Cow";
}

function keyOf(isoDate, time, type) {
    return `${isoDate}|${time}|${type}`;
}

// ---------------- UTC-safe date helpers ----------------
function isoToUtcMs(iso) {
    const s = String(iso || "").slice(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return NaN;
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return Date.UTC(y, mo, d);
}

function utcMsToIso(ms) {
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(
        d.getUTCDate()
    )}`;
}

function isoDateUTC(iso) {
    return new Date(String(iso).slice(0, 10) + "T00:00:00Z");
}

function addDaysIso(iso, days) {
    const d = isoDateUTC(iso);
    d.setUTCDate(d.getUTCDate() + Number(days || 0));
    return d.toISOString().slice(0, 10);
}

function ymLengthOfMonth(y, m1to12) {
    // m1to12: 1..12
    return new Date(Date.UTC(y, m1to12, 0)).getUTCDate();
}

function labelRange(date1, date2) {
    try {
        const ms1 = isoToUtcMs(date1);
        const ms2 = isoToUtcMs(date2);
        if (!Number.isFinite(ms1) || !Number.isFinite(ms2)) return `${date1} - ${date2}`;
        const d1 = new Date(ms1);
        const d2 = new Date(ms2);
        const fmt = (d) =>
            `${d.getUTCDate()} ${d.toLocaleString("en-US", { month: "short", timeZone: "UTC" })} ${d.getUTCFullYear()}`;
        return `${fmt(d1)} - ${fmt(d2)}`;
    } catch {
        return `${date1} - ${date2}`;
    }
}

// ---------------- period inference (fixes 11-20 & 21-ME) ----------------
const PeriodMode = {
    DECADAL_1_10_11_20_21_ME: "DECADAL_1_10_11_20_21_ME",
    HALF_1_15_16_ME: "HALF_1_15_16_ME",
    MONTH_1_ME: "MONTH_1_ME",
    QUIN_5DAY: "QUIN_5DAY",
    DECADAL_SHIFTED_6_16_26: "DECADAL_SHIFTED_6_16_26",
};

function normPeriodString(raw) {
    return String(raw || "")
        .replace(/\s+/g, "")
        .replace(/०/g, "0")
        .replace(/१/g, "1")
        .replace(/२/g, "2")
        .replace(/३/g, "3")
        .replace(/४/g, "4")
        .replace(/५/g, "5")
        .replace(/६/g, "6")
        .replace(/७/g, "7")
        .replace(/८/g, "8")
        .replace(/९/g, "9");
}

// fallback only if inference fails
function parsePeriod(raw) {
    const s = normPeriodString(raw).toLowerCase();

    const reDecadal = /^0?1-0?10,0?11-0?20,0?21-.*$/i;
    const reHalf = /^0?1-0?15,0?16-.*$/i;
    const reMonth = /^0?1-.*$/i;
    const reQuin =
        /^0?1-0?5,0?6-0?10,0?11-0?15,0?16-0?20,0?21-0?25,0?26-.*$/i;
    const reShifted = /^0?6-0?15,0?16-0?25,0?26-0?5$/i;

    if (reDecadal.test(s)) return { mode: PeriodMode.DECADAL_1_10_11_20_21_ME };
    if (reHalf.test(s)) return { mode: PeriodMode.HALF_1_15_16_ME };
    if (reQuin.test(s)) return { mode: PeriodMode.QUIN_5DAY };
    if (reShifted.test(s)) return { mode: PeriodMode.DECADAL_SHIFTED_6_16_26 };
    if (reMonth.test(s)) return { mode: PeriodMode.MONTH_1_ME };

    return { mode: PeriodMode.MONTH_1_ME };
}

function dayOfMonthUTC(iso) {
    return isoDateUTC(iso).getUTCDate();
}
function yearUTC(iso) {
    return isoDateUTC(iso).getUTCFullYear();
}
function monthUTC1to12(iso) {
    return isoDateUTC(iso).getUTCMonth() + 1;
}
function monthEndIsoUTC(iso) {
    const y = yearUTC(iso);
    const m = monthUTC1to12(iso);
    const me = ymLengthOfMonth(y, m);
    return `${y}-${pad2(m)}-${pad2(me)}`;
}
function isMonthEndUTC(iso) {
    return String(iso).slice(0, 10) === monthEndIsoUTC(iso);
}

// ✅ infer from last API range (this is what fixes your 11-20 & 21-ME)
function inferModeFromLastRange(lastStartIso, lastEndIso, periodSetting) {
    const sDay = dayOfMonthUTC(lastStartIso);
    const eDay = dayOfMonthUTC(lastEndIso);

    if (sDay === 1 && eDay === 10) return { mode: PeriodMode.DECADAL_1_10_11_20_21_ME };
    if (sDay === 1 && eDay === 15) return { mode: PeriodMode.HALF_1_15_16_ME };
    if (sDay === 1 && eDay === 5) return { mode: PeriodMode.QUIN_5DAY };

    // shifted 6/16/26 pattern
    if ((sDay === 6 && eDay === 15) || (sDay === 16 && eDay === 25) || (sDay === 26 && eDay === 5)) {
        return { mode: PeriodMode.DECADAL_SHIFTED_6_16_26 };
    }

    if (sDay === 1 && isMonthEndUTC(lastEndIso)) return { mode: PeriodMode.MONTH_1_ME };

    return parsePeriod(periodSetting);
}

function nextBucketEndFor(startIso, spec) {
    const d = isoDateUTC(startIso);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1; // 1..12
    const dom = d.getUTCDate();
    const me = ymLengthOfMonth(y, m);

    const isoOf = (yy, mm, dd) => `${yy}-${pad2(mm)}-${pad2(dd)}`;

    switch (spec.mode) {
        case PeriodMode.DECADAL_1_10_11_20_21_ME:
            if (dom <= 10) return isoOf(y, m, 10);
            if (dom <= 20) return isoOf(y, m, 20);
            return isoOf(y, m, me);

        case PeriodMode.HALF_1_15_16_ME:
            if (dom <= 15) return isoOf(y, m, 15);
            return isoOf(y, m, me);

        case PeriodMode.QUIN_5DAY:
            if (dom <= 5) return isoOf(y, m, 5);
            if (dom <= 10) return isoOf(y, m, 10);
            if (dom <= 15) return isoOf(y, m, 15);
            if (dom <= 20) return isoOf(y, m, 20);
            if (dom <= 25) return isoOf(y, m, 25);
            return isoOf(y, m, me);

        case PeriodMode.DECADAL_SHIFTED_6_16_26:
            if (dom >= 6 && dom <= 15) return isoOf(y, m, 15);
            if (dom >= 16 && dom <= 25) return isoOf(y, m, 25);
            if (dom >= 26) {
                const nm = new Date(Date.UTC(y, m - 1, 1));
                nm.setUTCMonth(nm.getUTCMonth() + 1);
                return `${nm.getUTCFullYear()}-${pad2(nm.getUTCMonth() + 1)}-05`;
            }
            return isoOf(y, m, 5);

        case PeriodMode.MONTH_1_ME:
        default:
            return isoOf(y, m, me);
    }
}

// ✅ extend until end of CURRENT period (not just today)
function buildExtendedRanges(existingRanges, periodSetting) {
    if (!Array.isArray(existingRanges) || existingRanges.length === 0) return [];

    // find max date2
    let last = null;
    for (const r of existingRanges) {
        const d1 = String(r.date1 || "").trim();
        const d2 = String(r.date2 || "").trim();
        if (!d1 || !d2) continue;
        if (!last || d2 > last.date2) last = { date1: d1, date2: d2 };
    }
    if (!last?.date2) return existingRanges;

    const spec = inferModeFromLastRange(last.date1, last.date2, periodSetting);

    // compute current period end based on TODAY (UTC-safe)
    const todayLocal = todayIsoLocal();                 // local yyyy-mm-dd
    const todayUtcIso = utcMsToIso(isoToUtcMs(todayLocal)); // normalize to UTC date
    const currentPeriodEnd = nextBucketEndFor(todayUtcIso, spec);

    const out = [...existingRanges];
    const seen = new Set(out.map((r) => `${r.date1}|${r.date2}`));

    // start next day after last end
    let pointer = addDaysIso(last.date2, 1);

    // safety guard
    let guard = 0;

    while (pointer <= currentPeriodEnd && guard < 500) {
        guard++;

        let end = nextBucketEndFor(pointer, spec);
        if (end > currentPeriodEnd) end = currentPeriodEnd;

        const k = `${pointer}|${end}`;
        if (!seen.has(k)) {
            out.push({ date1: pointer, date2: end, date_range: "" });
            seen.add(k);
        }

        if (end >= currentPeriodEnd) break;
        pointer = addDaysIso(end, 1);
    }

    // newest first
    out.sort((A, B) => {
        const c = String(B.date1 || "").localeCompare(String(A.date1 || ""));
        return c !== 0 ? c : String(B.date2 || "").localeCompare(String(A.date2 || ""));
    });

    return out;
}

// ---------------- component ----------------
export default function CollectionAdjustment({ mainuser }) {
     const navigate = useNavigate();        
    const customers = useCustomerRepo();
    const rates = useRateRepo();

    const periodSetting =
        localStorage.getItem("vikriperiodsettings") ||
        localStorage.getItem("paymentperiodsettings") ||
        "01-ME";

    const [rangeList, setRangeList] = useState([]);
    const [selRangeKey, setSelRangeKey] = useState("");
    const [fromIso, setFromIso] = useState("");
    const [toIso, setToIso] = useState("");

    const [kno, setKno] = useState("");
    const [cname, setCname] = useState("");
    const [mobno, setMobno] = useState("");

    const [shift, setShift] = useState("Morning"); // Morning / Evening / All
    const [milk, setMilk] = useState("Cow"); // Cow / Buffalo / All

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [tableSearch, setTableSearch] = useState("");

    const [allRows, setAllRows] = useState([]);
    const originalRowsRef = useRef(null);

    // cname below kno (overlay, no layout shift)
    useEffect(() => {
        const kn = String(kno || "").trim();
        if (!kn || !customers?.ready) {
            setCname("");
            setMobno("");
            return;
        }

        setCname(customers.getNameByKno?.(kn) || "");
        try {
            const mb = customers.getMobByKno?.(kn);
            setMobno(mb || "");
        } catch {
            setMobno("");
        }
    }, [kno, customers]);

    // ✅ load bill ranges once + extend (NO duplicate useEffect, NO "loads then erase")
    useEffect(() => {
        if (!mainuser) return;

        (async () => {
            try {
                const resp = await fetchBillDateRanges({
                    mainuser,
                    paymentperiodsettings: periodSetting,
                });

                let list = Array.isArray(resp?.rows) ? resp.rows : [];
                list = list
                    .map((r) => ({
                        date1: String(r?.date1 || "").trim(),
                        date2: String(r?.date2 || "").trim(),
                        date_range: "",
                    }))
                    .filter((r) => r.date1 && r.date2);

                // extend till current period end (and with inferred mode)
                list = buildExtendedRanges(list, periodSetting);

                // labels
                list = list.map((r) => ({
                    ...r,
                    date_range: labelRange(r.date1, r.date2),
                }));

                setRangeList(list);

                if (list.length) {
                    const newest = list[0];
                    const key = `${newest.date1}|${newest.date2}`;
                    setSelRangeKey(key);
                    setFromIso(newest.date1);
                    setToIso(newest.date2);
                } else {
                    setSelRangeKey("");
                    setFromIso("");
                    setToIso("");
                }
            } catch (e) {
                console.error("bill ranges error", e);
                setRangeList([]);
                setSelRangeKey("");
                setFromIso("");
                setToIso("");
            }
        })();
    }, [mainuser, periodSetting]);

    // build 4 slots/day grid
    function buildGrid(from, to, existingMap) {
        const out = [];
        const TIMES = ["Morning", "Evening"];
        const TYPES = ["Cow", "Buffalo"];

        const sMs = isoToUtcMs(from);
        const eMs = isoToUtcMs(to);
        if (!Number.isFinite(sMs) || !Number.isFinite(eMs)) return [];

        for (let ms = sMs; ms <= eMs; ms += 86400000) {
            const iso = utcMsToIso(ms);
            const d = new Date(ms);
            const dayNo = pad2(d.getUTCDate());

            for (const t of TIMES) {
                for (const ty of TYPES) {
                    const k = keyOf(iso, t, ty);
                    const hit = existingMap.get(k);
                    if (hit) {
                        out.push({
                            ...hit,
                            date: iso,
                            dayNo,
                            time: t,
                            type: ty,
                            isPlaceholder: false,
                            dirty: false,
                            status: "",
                        });
                    } else {
                        out.push({
                            date: iso,
                            dayNo,
                            time: t,
                            type: ty,
                            liter: "",
                            fat: "",
                            snf: "",
                            digri: "",
                            rate: "",
                            amt: "",
                            isPlaceholder: true,
                            dirty: false,
                            status: "",
                        });
                    }
                }
            }
        }

        out.sort((A, B) => {
            const c = A.date.localeCompare(B.date);
            if (c !== 0) return c;
            const ta = A.time === "Morning" ? 0 : 1;
            const tb = B.time === "Morning" ? 0 : 1;
            return ta - tb;
        });

        return out;
    }

    const visibleRows = useMemo(() => {
        const q = tableSearch.trim().toLowerCase();
        return allRows.filter((r) => {
            const okShift = shift === "All" ? true : String(r.time || "") === String(shift);
            const okMilk = milk === "All" ? true : String(r.type || "") === String(milk);
            if (!okShift || !okMilk) return false;

            if (!q) return true;
            return (
                String(r.dayNo || "").toLowerCase().includes(q) ||
                String(r.time || "").toLowerCase().includes(q) ||
                String(r.type || "").toLowerCase().includes(q) ||
                String(r.status || "").toLowerCase().includes(q)
            );
        });
    }, [allRows, shift, milk, tableSearch]);

    // rate calc
    function calcRateForRow(row) {
        const kn = String(kno || "").trim();
        if (!kn) return { rate: "", explain: "" };
        if (!customers?.ready || !rates?.ready) return { rate: "", explain: "" };

        const milkType = row.type === "Buffalo" ? "buf" : "cow";

        let chartNo = 1;
        try {
            chartNo = customers.getChartNoFor?.(kn, milkType) || 1;
        } catch {
            chartNo = 1;
        }

        const fat = Number(row.fat);
        const snf = Number(row.snf);
        const digri = Number(row.digri);

        if (!Number.isFinite(fat) || fat <= 0) return { rate: "", explain: "" };
        if (!Number.isFinite(snf) || snf <= 0) return { rate: "", explain: "" };

        const base = rates.getRate?.({
            type: milkType,
            fat,
            snf,
            digri: Number.isFinite(digri) ? digri : 0,
            ratechartno: chartNo,
        });

        if (base == null || !Number.isFinite(Number(base))) return { rate: "", explain: "" };

        const shiftExtra = 0;
        const finalRate = Number(base) + Number(shiftExtra);

        const explain = shiftExtra
            ? `Rate Chart (${Number(base).toFixed(2)}) + Shift Rate(${Number(shiftExtra).toFixed(2)})`
            : `Rate Chart (${Number(base).toFixed(2)})`;

        return { rate: finalRate.toFixed(2), explain };
    }

    function updateRowField(rowKey, field, value) {
        setAllRows((prev) =>
            prev.map((r) => {
                if (keyOf(r.date, r.time, r.type) !== rowKey) return r;

                const updated = { ...r, [field]: value, dirty: true };

                const { rate, explain } = calcRateForRow(updated);
                updated.rate = rate;

                const literN = Number(updated.liter || 0);
                const rateN = Number(rate || 0);
                updated.amt = literN > 0 && rateN > 0 ? (literN * rateN).toFixed(2) : "";

                updated.status = explain ? `${explain} ${r.isPlaceholder ? "(New)" : "(Edit)"}` : "";

                return updated;
            })
        );
    }

    async function onSearch() {
        const kn = String(kno || "").trim();
        if (!kn || !fromIso || !toIso) return;

        setLoading(true);
        try {
            const arr = await fetchSanklanByKnoRange({
                mainuser,
                kno: kn,
                fromIso,
                toIso,
            });

            const map = new Map();
            for (const o of Array.isArray(arr) ? arr : []) {
                const iso = String(o.date || "").trim();
                if (!iso) continue;

                const tRaw = mapTimeForServer(o.time);
                const tyRaw = mapTypeForServer(o.type);

                const t2 = tRaw === "Evening" ? "Evening" : "Morning";
                const ty2 = tyRaw === "Buffalo" ? "Buffalo" : "Cow";

                map.set(keyOf(iso, t2, ty2), {
                    date: iso,
                    time: t2,
                    type: ty2,
                    liter: Number(o.liter) > 0 ? fmt2(o.liter) : "",
                    fat: Number(o.fat) > 0 ? fmt1(o.fat) : "",
                    snf: Number(o.snf) > 0 ? fmt1(o.snf) : "",
                    digri: Number(o.digri || o.clr) > 0 ? fmt1(o.digri || o.clr) : "",
                    rate: Number(o.rate) > 0 ? fmt2(o.rate) : "",
                    amt: Number(o.amt) > 0 ? fmt2(o.amt) : "",
                });
            }

            const grid = buildGrid(fromIso, toIso, map);
            setAllRows(grid);
            originalRowsRef.current = JSON.parse(JSON.stringify(grid));
        } catch {
            setAllRows([]);
            originalRowsRef.current = null;
        } finally {
            setLoading(false);
        }
    }

    function onCancel() {
        setAllRows([]);
        originalRowsRef.current = null;
        setTableSearch("");
    }

    function onReset() {
        if (!originalRowsRef.current) return;
        setAllRows(JSON.parse(JSON.stringify(originalRowsRef.current)));
    }

    async function onSave() {
        if (saving) return;

        const kn = String(kno || "").trim();
        if (!kn) return;

        const dirtyRows = allRows.filter((r) => r.dirty);
        if (!dirtyRows.length) return;

        setSaving(true);
        try {
            for (const r of dirtyRows) {
                const literN = Number(r.liter || 0);
                const rateN = Number(r.rate || 0);
                if (!(literN > 0 && rateN > 0)) continue;

                const payload = {
                    mainuser: String(mainuser || ""),
                    date: isoToDmy(r.date),
                    date1: isoToDmy(r.date),
                    date2: isoToDmy(r.date),
                    type: mapTypeForServer(r.type),
                    time: mapTimeForServer(r.time),
                    kno: kn,
                    liter: numStrOrZero(r.liter),
                    fat: numStrOrZero(r.fat),
                    snf: numStrOrZero(r.snf),
                    digri: numStrOrZero(r.digri),
                    rate: numStrOrZero(r.rate),
                    amt: numStrOrZero(r.amt),
                    sredit: "0",
                    setting: "-",
                    mainsettings: String(localStorage.getItem("mainsettings") || ""),
                    msgonoff: String(localStorage.getItem("msgonoff") || "0"),
                    mobileuser: String(mainuser || ""),
                    flag: r.isPlaceholder ? "new" : "edit",
                };

                const text = await saveSanklan(payload);
                if (String(text || "").toLowerCase().includes("already exists")) {
                    alert(`Duplicate: ${r.date} ${r.time} ${r.type}`);
                    break;
                }
            }

            await onSearch();
        } catch (e) {
            alert(e?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    }

    function onRangeChange(val) {
        setSelRangeKey(val);
        const [d1, d2] = String(val || "").split("|");
        setFromIso(d1 || "");
        setToIso(d2 || "");
    }

    // UI styles (Farmer label inline + cname below without moving layout)
    const farmerWrapStyle = {
        position: "relative",
        display: "inline-block",
        minWidth: 180,
    };

    const cnameOverlayStyle = {
        position: "absolute",
        left: 0,
        top: "calc(100% + 4px)",
        fontSize: 12,
        opacity: 0.85,
        whiteSpace: "nowrap",
        pointerEvents: "none",
        maxWidth: 260,
        overflow: "hidden",
        textOverflow: "ellipsis",
    };

    const headerActionsStyle = {
        display: "flex",
        alignItems: "center",
        gap: 14,
        whiteSpace: "nowrap",
        flexWrap: "nowrap",
    };

    return (
        <div className="mdPage">
            <div className="mdPageTop">
                <div className="mdPageTitle" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button
                        type="button"
                        className="mdBackBtn"
                        onClick={() => window.history.back()}
                        aria-label="Back"
                        title="Back"
                    >
                        ←
                    </button>

                    <span className="mdPageTitleIcon">▣</span>
                    <span>Collection adjustment</span>
                </div>
            </div>


            <div
                className="mdFilters mdAdjFilters"
                style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 18 }}
            >
                <div className="mdFilter" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="mdFilterLabel">Date :</span>
                    <select className="mdSelect" value={selRangeKey} onChange={(e) => onRangeChange(e.target.value)}>
                        {rangeList.map((r) => {
                            const key = `${r.date1}|${r.date2}`;
                            return (
                                <option key={key} value={key}>
                                    {r.date_range}
                                </option>
                            );
                        })}
                    </select>
                </div>

                <div className="mdFilter" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="mdFilterLabel">Farmer :</span>
                    <div style={farmerWrapStyle}>
                        <input
                            className="mdInput mdInputSm"
                            value={kno}
                            onChange={(e) => setKno(e.target.value)}
                            placeholder="KNO"
                        />
                        <div style={cnameOverlayStyle}>{cname || "\u00A0"}</div>
                    </div>
                </div>

                <div className="mdFilter" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="mdFilterLabel">Shift :</span>
                    <select className="mdSelect" value={shift} onChange={(e) => setShift(e.target.value)}>
                        <option>All</option>
                        <option>Morning</option>
                        <option>Evening</option>
                    </select>
                </div>

                <div className="mdFilter" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="mdFilterLabel">Milk :</span>
                    <select className="mdSelect" value={milk} onChange={(e) => setMilk(e.target.value)}>
                        <option>All</option>
                        <option>Cow</option>
                        <option>Buffalo</option>
                    </select>
                </div>

                <button type="button" className="mdSearchBtn" onClick={onSearch} disabled={loading}>
                    {loading ? "Loading..." : "Search"}
                </button>

                <input type="hidden" value={mobno} readOnly />
            </div>

            <div className="mdCard">
                <div className="mdCardHead mdAdjHead">
                    <div className="mdCardHeadLeft">
                        <div className="mdCardCaption">
                            Collections <span className="mdNoteRed">(Note: Click on table cell to add/edit data)</span>
                        </div>
                    </div>

                    <div className="mdAdjActions" style={headerActionsStyle}>
                        <button type="button" className="mdLinkBtn" onClick={onCancel}>
                            ✕ Cancel
                        </button>
                        <button type="button" className="mdLinkBtn" onClick={onReset} disabled={!originalRowsRef.current}>
                            ↺ Reset
                        </button>
                        <button type="button" className="mdLinkBtn" onClick={onSave} disabled={saving || !allRows.length}>
                            💾 {saving ? "Saving..." : "Save"}
                        </button>
                    </div>

                    <div className="mdCardHeadRight">
                        <div className="mdSearchBox">
                            <span className="mdSearchLabel">Search</span>
                            <input
                                className="mdInput mdInputSm"
                                value={tableSearch}
                                onChange={(e) => setTableSearch(e.target.value)}
                                placeholder="Search"
                            />
                        </div>
                    </div>
                </div>

                <div className="mdTableWrap">
                    <table className="mdTable">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Shift</th>
                                <th>Milk</th>
                                <th className="mdNum">Quantity</th>
                                <th className="mdNum">FAT</th>
                                <th className="mdNum">SNF</th>
                                <th className="mdNum">Rate</th>
                                <th className="mdNum">Amount</th>
                                <th>Status</th>
                                <th className="mdActionCol">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleRows.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="mdEmpty">
                                        {loading ? "Loading..." : "No rows"}
                                    </td>
                                </tr>
                            ) : (
                                visibleRows.map((r) => {
                                    const k = keyOf(r.date, r.time, r.type);
                                    return (
                                        <tr key={k}>
                                            <td><b>{r.dayNo}</b></td>
                                            <td>{shiftShort(r.time)}</td>
                                            <td>{milkShort(r.type)}</td>

                                            <td className="mdNum">
                                                <input
                                                    className={`mdCellInput ${r.dirty ? "isDirty" : ""}`}
                                                    value={r.liter}
                                                    onChange={(e) => updateRowField(k, "liter", e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </td>

                                            <td className="mdNum">
                                                <input
                                                    className={`mdCellInput ${r.dirty ? "isDirty" : ""}`}
                                                    value={r.fat}
                                                    onChange={(e) => updateRowField(k, "fat", e.target.value)}
                                                    placeholder="0.0"
                                                />
                                            </td>

                                            <td className="mdNum">
                                                <input
                                                    className={`mdCellInput ${r.dirty ? "isDirty" : ""}`}
                                                    value={r.snf}
                                                    onChange={(e) => updateRowField(k, "snf", e.target.value)}
                                                    placeholder="0.0"
                                                />
                                            </td>

                                            <td className="mdNum">{r.rate ? fmt2(r.rate) : ""}</td>
                                            <td className="mdNum">{r.amt ? fmt2(r.amt) : ""}</td>

                                            <td className="mdStatus">{r.status}</td>

                                            <td className="mdActionCol">
                                                {!r.isPlaceholder && Number(r.liter) > 0 ? (
                                                    <button type="button" className="mdIconBtn" title="Row exists">
                                                        🗑
                                                    </button>
                                                ) : (
                                                    <span style={{ opacity: 0.3 }}>-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mdSmallNote">
                    Customers ready: {customers?.ready ? "Yes" : "No"} • Rates ready: {rates?.ready ? "Yes" : "No"}
                </div>
            </div>
        </div>
    );
}
